import { useEffect } from "react";
import HeroSection from "./components/HeroSection";
import ServicesSection from "./components/ServicesSection";
import ZonesSection from "./components/ZonesSection";
import ReviewsSection from "./components/ReviewsSection";
import AboutSection from "./components/AboutSection";
import FaqSection from "./components/FaqSection";
import CtaSection from "./components/CtaSection";

const SITE_URL = import.meta.env.VITE_SITE_URL || "https://example.com";

export default function HomePage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = "Securycles — Serrurier Urgence Créteil Val-de-Marne 24h/24";

    const setMeta = (name: string, content: string, prop = false) => {
      const attr = prop ? "property" : "name";
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("description", "Securycles, serrurier d'urgence à Créteil et dans tout le Val-de-Marne (94). Ouverture de porte, serrure connectée, dépannage auto. Intervention en 20 min, disponible 24h/24 et 7j/7. Appelez le 06 04 02 68 40.");
    setMeta("keywords", "serrurier Créteil, serrurier urgence 94, ouverture porte Val-de-Marne, serrure connectée Créteil");
    setMeta("robots", "index, follow");
    setMeta("last-modified", "2026-04-28");
    setMeta("author", "Securycles");

    // Geo SEO
    setMeta("geo.region", "FR-94");
    setMeta("geo.placename", "Créteil, Val-de-Marne, Île-de-France");
    setMeta("geo.position", "48.7904;2.4556");
    setMeta("ICBM", "48.7904, 2.4556");

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", SITE_URL + "/");

    // Open Graph
    setMeta("og:type", "website", true);
    setMeta("og:title", "Securycles — Serrurier Urgence Créteil Val-de-Marne 24h/24", true);
    setMeta("og:description", "Serrurier d'urgence à Créteil et dans tout le Val-de-Marne. Intervention en 20 min, 24h/24 et 7j/7. Ouverture de porte, serrure connectée, dépannage auto.", true);
    setMeta("og:url", SITE_URL + "/", true);
    setMeta("og:image", "https://static.readdy.ai/image/8892c16dc9d95083db2eef95f931ffc6/474014a289bcb6e7215bd7741beb01c1.png", true);
    setMeta("og:locale", "fr_FR", true);
    setMeta("og:site_name", "Securycles", true);

    // Twitter Card
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "Securycles — Serrurier Urgence Créteil 24h/24");
    setMeta("twitter:description", "Serrurier d'urgence à Créteil et Val-de-Marne. Intervention 20 min. 06 04 02 68 40");
    setMeta("twitter:image", "https://static.readdy.ai/image/8892c16dc9d95083db2eef95f931ffc6/474014a289bcb6e7215bd7741beb01c1.png");

    // Schema.org LocalBusiness (Locksmith)
    const schemaId = "schema-homepage";
    let existing = document.getElementById(schemaId);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Locksmith",
      "name": "Securycles",
      "description": "Serrurier d'urgence à Créteil et dans tout le Val-de-Marne. Ouverture de porte, serrure connectée, dépannage auto. Disponible 24h/24 et 7j/7.",
      "url": SITE_URL,
      "logo": "https://static.readdy.ai/image/8892c16dc9d95083db2eef95f931ffc6/474014a289bcb6e7215bd7741beb01c1.png",
      "image": "https://static.readdy.ai/image/8892c16dc9d95083db2eef95f931ffc6/474014a289bcb6e7215bd7741beb01c1.png",
      "telephone": "+33604026840",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Créteil",
        "postalCode": "94000",
        "addressRegion": "Val-de-Marne",
        "addressCountry": "FR"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 48.7904,
        "longitude": 2.4556
      },
      "areaServed": {
        "@type": "AdministrativeArea",
        "name": "Val-de-Marne (94)"
      },
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"],
          "opens": "00:00",
          "closes": "23:59"
        }
      ],
      "priceRange": "€€",
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.9",
        "reviewCount": "87",
        "bestRating": "5"
      },
      "sameAs": [
        "https://www.tiktok.com/@securycles.serrur",
        "https://maps.app.goo.gl/kadTYV8afmpfdVSu8"
      ]
    });
    document.head.appendChild(script);

    // Schema.org WebSite
    const webSchemaId = "schema-website";
    let webExisting = document.getElementById(webSchemaId);
    if (webExisting) webExisting.remove();
    const webScript = document.createElement("script");
    webScript.id = webSchemaId;
    webScript.type = "application/ld+json";
    webScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Securycles",
      "url": SITE_URL,
      "description": "Serrurier d'urgence à Créteil et dans tout le Val-de-Marne. Ouverture de porte, serrure connectée, dépannage auto. Disponible 24h/24 et 7j/7.",
      "inLanguage": "fr-FR",
      "publisher": {
        "@type": "LocalBusiness",
        "name": "Securycles"
      }
    });
    document.head.appendChild(webScript);

    // Schema.org FAQPage
    const faqSchemaId = "schema-faqpage";
    let faqExisting = document.getElementById(faqSchemaId);
    if (faqExisting) faqExisting.remove();
    const faqScript = document.createElement("script");
    faqScript.id = faqSchemaId;
    faqScript.type = "application/ld+json";
    faqScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "Quel est le délai d'intervention de Securycles à Créteil ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Securycles intervient en moyenne en 20 minutes à Créteil et dans tout le Val-de-Marne, 24h/24 et 7j/7."
          }
        },
        {
          "@type": "Question",
          "name": "Securycles intervient-il dans tout le Val-de-Marne ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, Securycles couvre l'ensemble des communes du département 94 : Créteil, Vitry-sur-Seine, Saint-Maur-des-Fossés, Champigny-sur-Marne, Ivry-sur-Seine, Maisons-Alfort et toutes les autres villes du Val-de-Marne."
          }
        },
        {
          "@type": "Question",
          "name": "Securycles installe-t-il des serrures connectées ?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Oui, Securycles est spécialisé dans l'installation et la configuration de serrures connectées (Nuki, Yale, Somfy) à Créteil et dans le Val-de-Marne."
          }
        }
      ]
    });
    document.head.appendChild(faqScript);

    // Schema.org BreadcrumbList
    const bcSchemaId = "schema-breadcrumb";
    let bcExisting = document.getElementById(bcSchemaId);
    if (bcExisting) bcExisting.remove();
    const bcScript = document.createElement("script");
    bcScript.id = bcSchemaId;
    bcScript.type = "application/ld+json";
    bcScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Accueil",
          "item": SITE_URL
        }
      ]
    });
    document.head.appendChild(bcScript);

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
      const ws = document.getElementById(webSchemaId);
      if (ws) ws.remove();
      const fs = document.getElementById(faqSchemaId);
      if (fs) fs.remove();
      const bcs = document.getElementById(bcSchemaId);
      if (bcs) bcs.remove();
    };
  }, []);

  return (
    <main>
      <HeroSection />
      <ServicesSection />
      <ZonesSection />
      <ReviewsSection />
      <AboutSection />
      <FaqSection />
      <CtaSection />
    </main>
  );
}
